import type { CategoryIcon } from '@bifeai/shared';
import { Button } from '@bifeai/ui';
import { Camera, ImagePlus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { ProductThumb } from '../../components/inventory';
import { preparePhoto, type PreparedPhoto } from '../../lib/image';

/** El celular o la tablet ofrecen sacar la foto con la cámara directamente. */
function isTouchDevice(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/**
 * Elegir una foto de la compu o sacarla con la cámara del celular.
 * La foto se achica acá mismo antes de subirse.
 */
export function PhotoPicker({
  currentUrl,
  icon,
  name,
  pending,
  onPick,
  onRemove,
  busy,
}: {
  currentUrl: string | null;
  icon: CategoryIcon | null;
  name: string;
  pending: PreparedPhoto | null;
  onPick: (photo: PreparedPhoto | null) => void;
  onRemove?: () => void;
  busy?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const touch = isTouchDevice();

  useEffect(
    () => () => {
      if (pending) URL.revokeObjectURL(pending.previewUrl);
    },
    [pending],
  );

  async function handle(file: File | undefined) {
    if (!file) return;
    setError(null);
    setProcessing(true);
    try {
      onPick(await preparePhoto(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo usar esa imagen.');
    } finally {
      setProcessing(false);
      if (fileRef.current) fileRef.current.value = '';
      if (cameraRef.current) cameraRef.current.value = '';
    }
  }

  const shown = pending?.previewUrl ?? currentUrl;

  return (
    <div className="bf-field">
      <span className="bf-label">Foto del producto</span>
      <div className="bf-photo-picker">
        <ProductThumb photoUrl={shown} icon={icon} name={name || 'Producto'} />
        <div className="bf-photo-picker-actions">
          <div>
            <Button size="sm" icon={<ImagePlus />} loading={processing} disabled={busy} onClick={() => fileRef.current?.click()}>
              {shown ? 'Cambiar foto' : 'Subir foto'}
            </Button>
            {touch && (
              <Button size="sm" icon={<Camera />} disabled={busy || processing} onClick={() => cameraRef.current?.click()}>
                Sacar foto
              </Button>
            )}
            {pending && (
              <Button size="sm" variant="ghost" onClick={() => onPick(null)} disabled={busy}>
                Descartar
              </Button>
            )}
            {!pending && currentUrl && onRemove && (
              <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={onRemove} disabled={busy}>
                Quitar
              </Button>
            )}
          </div>
          <p className={error ? 'bf-field-error' : 'bf-hint'} role={error ? 'alert' : undefined}>
            {error ?? (pending ? 'Se guarda al confirmar.' : 'JPG, PNG o WebP. La achicamos automáticamente.')}
          </p>
        </div>
      </div>
      <input
        ref={fileRef}
        className="bf-file-input"
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        onChange={(e) => void handle(e.target.files?.[0])}
      />
      <input
        ref={cameraRef}
        className="bf-file-input"
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => void handle(e.target.files?.[0])}
      />
    </div>
  );
}
